import React, { useEffect, useState } from "react";
import { getAllServices } from "../../services/serviceService";
import { BiggerCriminalDetail } from "../../interfaces/criminalDetail";
import { Service } from "../../interfaces/service";
import { getVendors, refreshVendorData } from "../../services/vendorService";
import { Vendor } from "../../interfaces/vendor";
import { useNavigate } from "react-router-dom";
import { formatDate } from "../../services/helpers";
import { getCompletedCriminalDetails } from "../../services/criminalDetailService";
import ToolTip from "../../components/component-tools/ToolTip";

const ReviewQueue = () => {
  const [loading, setLoading] = useState(true);
  const [services, setServices] = useState<Service[]>([]);
  const [completedCriminalDetails, setCompletedCriminalDetails] = useState<
    BiggerCriminalDetail[]
  >([]);
  const [vendors, setVendors] = useState<Vendor[]>([]);
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");
  const [vendorID, setVendorID] = useState<number>(0);
  const [appliedVendorID, setAppliedVendorID] = useState<number>(0);
  const [showFilter, setShowFilter] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);

        const servicesResponse = await getAllServices();
        setServices(servicesResponse);

        const vendorsResponse = await getVendors();
        const sortedVendors = vendorsResponse.filter((v) => v.isActive);
        setVendors(sortedVendors);

        const detailsResponse = await getCompletedCriminalDetails();
        setCompletedCriminalDetails(detailsResponse);
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const filteredDetails = completedCriminalDetails
    .filter((detail): detail is NonNullable<typeof detail> => detail !== null)
    .filter(
      (detail) =>
        detail.candidateFirstName
          .toLowerCase()
          .includes(searchTerm.toLowerCase()) ||
        detail.candidateLastName
          .toLowerCase()
          .includes(searchTerm.toLowerCase()),
    )
    .filter(
      (detail) => appliedVendorID === 0 || detail.vendorID === appliedVendorID,
    );

  const applyFilter = () => {
    setAppliedVendorID(vendorID);
    setShowFilter(false);
  };

  const resetFilter = () => {
    setVendorID(0);
    setAppliedVendorID(0);
  };

  const HandleRefreshVendorData = async () => {
    try {
      setLoading(true);

      await refreshVendorData();

      const detailsResponse = await getCompletedCriminalDetails();
      setCompletedCriminalDetails(detailsResponse);

      window.location.reload();
    } catch (error) {
      console.error("Error refreshing vendor data:", error);
      setErrorMessage("Error refreshing vendor data.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="content-container">
      <div className="header-container">
        <h1 style={{ display: "flex" }}>
          Vendor Review Queue{" "}
          <ToolTip
            tooltipText="All orders that have received a response from a vendor and have NOT been admin reviewed (if they are able to be reviewed i.e. fama and sub add-on)."
            placement="right"
            className="info-icon"
          />
        </h1>

        <div className="page-items-container">
          <button
            className="btn-funnel"
            onClick={() => setShowFilter(!showFilter)}
          >
            <i
              className={`bi bi-funnel-fill filter-icon ${
                appliedVendorID ? "active-filter-icon" : ""
              }`}
            ></i>
          </button>
          <div className="search-container">
            <input
              style={{ border: "none", width: "100%", outline: "none" }}
              type="text"
              placeholder="Search Candidates..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            {searchTerm.trim() ? (
              <i
                className="bi bi-x-circle clear-icon"
                onClick={() => setSearchTerm("")}
                style={{ cursor: "pointer" }}
              ></i>
            ) : (
              <i className="bi bi-search search-icon"></i>
            )}
          </div>
          <div style={{ cursor: "pointer", display: "flex" }}>
            <button
              className="oxbtn btn-grey"
              onClick={() => HandleRefreshVendorData()}
            >
              Refresh Vendor Data
            </button>
            <div>
              <ToolTip
                tooltipText="This will pull fresh data from all vendors for initiated orders."
                placement="left"
                className="info-icon"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Filter Popup */}
      {showFilter && (
        <>
          <div
            className="background-mask"
            onClick={() => setShowFilter(false)}
          ></div>
          <div className="filter-popup">
            <div className="close-button-container">
              <button
                className="close-button"
                onClick={() => setShowFilter(false)}
              >
                <i className="bi bi-x-circle"></i>
              </button>
            </div>
            <div className="filter-header">
              <h2>Filter by Vendor</h2>
            </div>
            <div className="filter-body">
              <div className="field-stacked" style={{ flex: "0 0 30%" }}>
                <label className="field-label">Vendor:</label>
                <div className="field-input">
                  <select
                    className="form-control"
                    value={vendorID}
                    onChange={(e) => setVendorID(Number(e.target.value))}
                  >
                    <option value={0}>All Vendors</option>
                    {vendors.map((vendor) => (
                      <option key={vendor.vendorID} value={vendor.vendorID}>
                        {vendor.code}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
            <div className="filter-actions">
              <button className="smbtn btn-grey" onClick={resetFilter}>
                Reset
              </button>
              <button
                className={`smbtn btn-green ${
                  vendorID === 0 ? "disabled" : ""
                }`}
                onClick={applyFilter}
                disabled={vendorID === 0}
              >
                Apply
              </button>
            </div>
          </div>
        </>
      )}

      {loading ? (
        <div className="loading-divider">
          <div className="loading-bar"></div>
        </div>
      ) : (
        <>
          <div className="divider"></div>
          <div className="table-wrapper">
            <div className="table-container">
              <table
                className="table"
                style={{
                  cursor: "pointer",
                }}
              >
                <thead>
                  <tr>
                    <th style={{ width: "200px" }}>Candidate Name</th>
                    <th style={{ width: "200px" }}>Service</th>
                    <th style={{ width: "150px" }}></th>
                    <th style={{ width: "150px" }}></th>
                    <th style={{ width: "100px" }}>Initiated</th>
                    <th style={{ width: "100px" }}>Received</th>
                    <th style={{ width: "200px" }}>Vendor</th>
                    <th style={{ width: "50px" }}>Fee</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredDetails?.length === 0 ? (
                    <tr>
                      <td colSpan={8}>No Completed Vendor Orders.</td>
                    </tr>
                  ) : (
                    filteredDetails?.map((detail) => {
                      return (
                        <tr
                          key={detail?.criminalDetailID}
                          onClick={() =>
                            navigate(
                              `/admin/order-service/${detail.criminalDetailID}`,
                              {
                                state: {
                                  criminalDetailID: detail.criminalDetailID,
                                },
                              },
                            )
                          }
                        >
                          <td>
                            {detail.candidateFirstName}{" "}
                            {detail.candidateLastName}
                          </td>
                          <td>
                            {services.find(
                              (s) => s.serviceID === detail.serviceID,
                            )?.serviceName ?? "Unknown Service"}
                          </td>
                          <td>{detail?.resultsFieldOne}</td>
                          <td>{detail?.resultsFieldTwo}</td>
                          <td>{formatDate(detail?.initiatedDate, "us")}</td>
                          <td>{formatDate(detail?.receivedDate, "us")}</td>
                          <td>
                            {vendors.find((v) => v.vendorID === detail.vendorID)
                              ?.code ?? "Unknown Vendor"}
                          </td>
                          <td>{detail?.fee}</td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
      {errorMessage && (
        <div
          className="alert-message alert alert-danger alert-dismissible fade show"
          role="alert"
        >
          {errorMessage}
          <button
            type="button"
            className="btn-close"
            onClick={() => setErrorMessage("")}
            aria-label="Close"
          ></button>
        </div>
      )}
    </div>
  );
};

export default ReviewQueue;
